import * as UI from "@iyk/ui"
import * as React from "react"

import { each } from "@iyk/flow"
import { useIntersection } from "../ui/use-intersection.ts"
import { useWatchSize } from "../ui/use-watch-size.ts"
import { Video } from "../ui/video.tsx"
import { Branded } from "./branded.tsx"
import { SectionLayout } from "./ui/section-layout.tsx"

export function GrabAPostcard() {
  const [isVisible, setIsVisible] = React.useState(false)

  const containerRef = useIntersection<HTMLDivElement>({
    onIntersect: (entry) => {
      if (entry.isIntersecting) {
        setIsVisible(true)
      }
    },
    options: {
      threshold: 0.8,
    },
  })

  const { ref: sizeRef, size: containerSize } = useWatchSize()

  return (
    <SectionLayout size="fluid" theme="dark" className="text-white">
      <div
        ref={UI.extendRef(containerRef, sizeRef)}
        className="h-svh relative flex flex-col items-center justify-center overflow-hidden"
        onClick={() => setIsVisible(true)}
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="text-xs slashed-zero relative px-2" aria-hidden="true">
            <CountUp />
            <div className="size-1 bg-tomato-11 rounded-full absolute right-0 top-0 animate-pulse" />
          </div>
          <p className="text-xl">
            Grab a <Branded with=" [*]">Postcard</Branded> and tap to say...
          </p>
        </div>
        {containerSize &&
          squares.map((square) => (
            <Square
              key={square.id}
              square={square}
              animate={isVisible}
              containerSize={containerSize}
            />
          ))}
      </div>
    </SectionLayout>
  )
}

const CountUp = () => {
  const [time, setTime] = React.useState(0)

  React.useEffect(() => {
    const start = performance.now()
    const interval = setInterval(() => {
      setTime(Math.floor(performance.now() - start))
    }, 10)

    return () => clearInterval(interval)
  }, [])

  const formatTime = (ms: number) => {
    const minutes = String(Math.floor(ms / 60000)).padStart(2, "0")
    const seconds = String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")
    const milliseconds = String(Math.floor((ms % 1000) / 10)).padStart(2, "0")

    return `${minutes}:${seconds}:${milliseconds}`
  }

  return <span className="tabular-nums">{formatTime(time)}</span>
}

const Square = function Square({ square, animate, containerSize }: SquareProps) {
  const elementRef = React.useRef<HTMLDivElement>(null)
  const animationRef = React.useRef<Animation>()

  const start = {
    x: containerSize.width / 2,
    y: containerSize.height / 2,
  }
  const target = {
    x: (square.x / 100) * containerSize.width,
    y: (square.y / 100) * containerSize.height,
  }
  const delay = React.useMemo(() => Math.round(Math.random() * 350), [])

  React.useEffect(() => {
    if (!elementRef.current || !animate) return

    animationRef.current?.cancel()

    const animation = elementRef.current.animate(
      [
        {
          transform: `translate(${start.x}px, ${start.y}px) scale(0)`,
          opacity: 0,
        },
        {
          transform: `translate(${target.x}px, ${target.y}px) scale(1) `,
          opacity: 1,
        },
      ],
      {
        duration: 700,
        easing: "cubic-bezier(0.08,0.82,0.17,1)",
        fill: "forwards",
        delay,
      },
    )

    animationRef.current = animation

    const handleAnimationFinish = () => {
      try {
        animation.commitStyles()
        animation.cancel()
      } catch {}
    }

    animation.addEventListener("finish", handleAnimationFinish)

    return () => {
      animation.removeEventListener("finish", handleAnimationFinish)
      animation.cancel()
    }
  }, [animate])

  React.useEffect(() => {
    if (!elementRef.current || !animate) return
    elementRef.current.style.transform = `translate(${target.x}px, ${target.y}px)`
  }, [target.x, target.y])

  return (
    <div
      ref={elementRef}
      className={classNameForSquare({ hiddenOnMobile: square.hiddenOnMobile })}
      style={{
        transform: `translate(${start.x}px, ${start.y}px)`,
        opacity: 0,
      }}
    >
      <Video className="border-[3px] border-white size-24 object-cover" aria-hidden="true">
        {each(square.srcset, ({ src, type }) => (
          <source key={src} src={src} type={type} />
        ))}
      </Video>
      <p className="text-xs text-white bg-black px-1.5 py-0.5">{square.name}</p>
    </div>
  )
}

const classNameForSquare = UI.cva(
  ["flex flex-col items-center gap-2 justify-center absolute top-0 left-0"],
  {
    variants: {
      hiddenOnMobile: {
        true: ["hidden md:flex"],
      },
    },
  },
)

type Square = {
  id: number
  x: number // percentage
  y: number // percentage
  srcset: SquareSrcSet[]
  name: string
  hiddenOnMobile?: boolean
}

type SquareSrcSet = { type: string; src: string }

type SquareProps = {
  square: Square
  animate: boolean
  containerSize: { width: number; height: number }
}
const squares: Array<Square> = [
  {
    id: 1,
    x: 3,
    y: 2,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/Aaron-self-video-1x1.mp4",
      },
    ],
    name: "happy_holidays.mp4",
  },
  {
    id: 2,
    x: 25,
    y: -3,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/Anam-self-video-1x1.mp4",
      },
    ],
    name: "merry_xmas.mp4",
  },
  {
    id: 3,
    x: 70,
    y: 15,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/Austin-self-video-1x1.mp4",
      },
    ],
    name: "santa_szn.mp4",
  },
  {
    id: 4,
    x: 14,
    y: 40,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/Brandy-self-video-1x1.mp4",
      },
    ],
    name: "besties_4ever.mp4",
    hiddenOnMobile: true,
  },
  {
    id: 5,
    x: 45,
    y: 28,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/Dasha-self-video-1x1.mp4",
      },
    ],
    name: "see_you_soon.mp4",
    hiddenOnMobile: true,
  },
  {
    id: 6,
    x: 87,
    y: 40,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/misc1-self-video-1x1.mp4",
      },
    ],
    name: "we_miss_u.mp4",
    hiddenOnMobile: true,
  },
  {
    id: 7,
    x: 1,
    y: 69,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/Dongjin-self-video-1x1.mp4",
      },
    ],
    name: "love_u_sis.mp4",
    hiddenOnMobile: true,
  },
  {
    id: 11,
    x: 47,
    y: 67,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/Ryan-self-video-1x1.mp4",
      },
    ],
    name: "hiii.mp4",
  },
  {
    id: 8,
    x: 45,
    y: 60,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/jamie-self-video-1x1.mp4",
      },
    ],
    name: "my_heart.mp4",
    hiddenOnMobile: true,
  },
  {
    id: 9,
    x: 94,
    y: 4,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/luz-self-video-1x1.mp4",
      },
    ],
    name: "wish_u_were_here.mp4",
    hiddenOnMobile: true,
  },
  {
    id: 10,
    x: 20,
    y: 75,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/paff-self-video-1x1.mp4",
      },
    ],
    name: "luv_our_family_traditions.mp4",
  },
  {
    id: 12,
    x: 75,
    y: 80,
    srcset: [
      {
        type: "video/mp4",
        src: "https://assets.iyk-dev.app/postcards/grab-a-postcard/misc3-self-video-1x1.mp4",
      },
    ],
    name: "first_snow!!!.mp4",
  },
]
